import { useEffect, useState } from 'react';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import { Loader2, Pencil } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

interface CEOData {
  name: { ar: string; en: string };
  credentials: { ar: string; en: string };
  image: string;
  email?: string;
}

export function CEOSection() {
  const [ceoData, setCeoData] = useState<CEOData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: { ar: '', en: '' },
    credentials: { ar: '', en: '' },
    image: '',
    email: ''
  });

  useEffect(() => {
    fetchCEOData();
  }, []);

  const fetchCEOData = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'landing_ceo'));
      
      if (!querySnapshot.empty) {
        const data = querySnapshot.docs[0].data() as CEOData;
        setCeoData(data);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching CEO data:', error);
      toast.error('حدث خطأ أثناء تحميل بيانات المدير التنفيذي');
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (ceoData) {
      setFormData({
        name: typeof ceoData.name === 'string' ? { ar: ceoData.name, en: '' } : ceoData.name,
        credentials: typeof ceoData.credentials === 'string' ? { ar: ceoData.credentials, en: '' } : ceoData.credentials,
        image: ceoData.image,
        email: ceoData.email || ''
      });
      setIsEditModalOpen(true);
    }
  };

  const handleSave = async () => {
    try {
      await updateDoc(doc(db, 'landing_ceo', 'ceo'), {
        name: formData.name,
        credentials: formData.credentials,
        image: formData.image,
        email: formData.email,
        updatedAt: new Date()
      });

      toast.success('تم تحديث بيانات المدير التنفيذي بنجاح');
      setIsEditModalOpen(false);
      fetchCEOData();
    } catch (error) {
      console.error('Error updating CEO data:', error);
      toast.error('حدث خطأ أثناء تحديث بيانات المدير التنفيذي');
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  if (!ceoData) {
    return (
      <Card className="p-6">
        <p className="text-center text-muted-foreground">لا توجد بيانات للمدير التنفيذي</p>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">بيانات المدير التنفيذي</h3>
          <Button onClick={handleEdit} size="sm">
            <Pencil className="h-4 w-4 ml-2" />
            تعديل
          </Button>
        </div>
        
        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-[23%]">
            <img
              src={ceoData.image}
              alt={typeof ceoData.name === 'string' ? ceoData.name : ceoData.name.ar}
              className="w-full rounded-lg object-cover shadow-md"
            />
          </div>
          <div className="flex-1">
            <h4 className="text-2xl font-bold text-[#004250] mb-4">{typeof ceoData.name === 'string' ? ceoData.name : ceoData.name.ar}</h4>
            {ceoData.email && (
              <p className="text-lg text-[#7dda55] font-medium mb-4">{ceoData.email}</p>
            )}
            <p className="text-gray-600 whitespace-pre-line leading-relaxed">{typeof ceoData.credentials === 'string' ? ceoData.credentials : ceoData.credentials.ar}</p>
          </div>
        </div>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">تعديل بيانات المدير التنفيذي</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <Label className="text-base font-semibold">الاسم / Name</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="name-ar"
                    value={formData.name.ar}
                    onChange={(e) => setFormData({ ...formData, name: { ...formData.name, ar: e.target.value } })}
                    className="text-right"
                    dir="rtl"
                    placeholder="د. محمد أحمد"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="name-en"
                    value={formData.name.en}
                    onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })}
                    dir="ltr"
                    placeholder="Dr. Mohammed Ahmed"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">البريد الإلكتروني</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="text-left"
                dir="ltr"
                placeholder="ceo@namat.org.sa"
              />
            </div>
            <div className="space-y-3">
              <Label className="text-base font-semibold">السيرة الذاتية والمؤهلات / Credentials</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="credentials-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Textarea
                    id="credentials-ar"
                    value={formData.credentials.ar}
                    onChange={(e) => setFormData({ ...formData, credentials: { ...formData.credentials, ar: e.target.value } })}
                    className="min-h-[200px] text-right"
                    dir="rtl"
                    placeholder="خبير في مجال الصحة العامة..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="credentials-en" className="text-sm text-muted-foreground">English</Label>
                  <Textarea
                    id="credentials-en"
                    value={formData.credentials.en}
                    onChange={(e) => setFormData({ ...formData, credentials: { ...formData.credentials, en: e.target.value } })}
                    className="min-h-[200px]"
                    dir="ltr"
                    placeholder="Expert in public health..."
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="image">رابط الصورة</Label>
              <Input
                id="image"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                className="text-left"
                dir="ltr"
                placeholder="https://example.com/image.jpg"
              />
              {formData.image && (
                <img
                  src={formData.image}
                  alt="Preview"
                  className="w-64 rounded-lg object-cover mt-2"
                />
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                إلغاء
              </Button>
              <Button onClick={handleSave}>
                حفظ التغييرات
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

